import React from 'react'
import PropTypes from 'prop-types'
import ErrorsContext from './contexts/ErrorsContext'
import FormContext from './contexts/FormContext'
import useResource from './hooks/use-resource'
import useOperation from './hooks/use-operation'
import useOperations from './hooks/use-operations'
import useModel from './hooks/use-model'
import { CallbackOnPipe, getMapValue, Pipeline, useEventListeners } from '@morningtrain/react-helpers'
import { observable } from 'mobx'
import ResetFormOnPipe from './pipes/ResetFormOnPipe'
import TriggerPipelineOnFormStartSubmit from './pipes/TriggerPipelineOnFormStartSubmit'

function Form (props) {
  const resource = useResource()
  const operations = useOperations()
  const parentOperation = useOperation()
  const model = useModel()

  const formRef = React.createRef()

  const isChangedRef = React.useRef(false)

  const pipelineResolveRef = React.useRef()
  const pipelineRejectRef = React.useRef()

  const [triggerOnStartSubmit, onStartSubmit, removeOnStartSubmitListener] = useEventListeners()
  const [triggerOnSubmit, onSubmit, removeOnSubmitListener] = useEventListeners()
  const [triggerOnReset, onReset, removeOnResetListener] = useEventListeners()

  let operation = null
  if (props.route) {
    operation = operations.getFromRoute(props.route)
  } else if (resource) {
    operation = operations.get(resource.namespace, resource.slug, props.submitTo)
  }

  function performSubmit (extraData = null, callback, abortCallback) {
    if (props.withoutOperation === false && operation && operation.executing === true) {
      // TODO - Maybe we should abort current execution and make a new one instead?
      if (typeof abortCallback === 'function') {
        abortCallback()
      }
      return false
    }

    const data = new FormData(formRef.current)

    if (extraData !== null) {
      if (typeof data.set === 'function') {
        Object.keys(extraData).forEach(extraDataKey => {
          data.set(extraDataKey, extraData[extraDataKey])
        })
      }
    }

    /// TODO - Make this less hardcoded and less error prone if not providing model and operation
    const params = {}

    if (model !== null) {
      if (operation) {
        params[operation.parameterKey] = getMapValue(model, operation.modelKey)
      } else if (props.modelKeyName && props.parameterKeyName) {
        params[props.parameterKeyName] = getMapValue(model, props.modelKeyName)
      }
    }

    triggerOnStartSubmit(Object.fromEntries(data))

    if (props.withoutOperation === false && operation) {
      operation.execute(params, data)
        .then((data) => {
          if (typeof (props.onSubmit) === 'function') {
            props.onSubmit(data)
          }

          triggerOnSubmit(data)

          if (typeof callback === 'function') {
            callback()
          }

          return data
        })
        .then((data) => {
          setAsChanged(false)

          if (typeof pipelineResolveRef.current === 'function') {
            pipelineResolveRef.current({ response: data })
          }

          /// If the submitTo operation is different from the originally provided operation
          /// Then we can assume that we might need to refresh the original operation to provider data to our children
          if (!props.preventParentOperationRefresh && parentOperation && parentOperation.slug !== operation.slug) {
            return parentOperation.refresh()
          }

          return data
        })
        .catch(err => {
          if (typeof pipelineRejectRef.current === 'function') {
            pipelineRejectRef.current(err)
          }
          if (typeof abortCallback === 'function') {
            abortCallback(err)
          }
        })
    }
  }

  const handleOnSubmit = (e) => {
    e.preventDefault()
    e.stopPropagation()

    performSubmit()
  }

  function reset () {
    return new Promise((resolve, reject) => {
      if (formRef && formRef.current && typeof formRef.current.reset === 'function') {
        formRef.current.reset()
      }

      triggerOnReset()

      resolve()
    })
  }

  function submit (extraData) {
    return new Promise((resolve, reject) => {
      performSubmit(extraData, () => {
        resolve()
      }, err => {
        reject(err)
      })
    })
  }

  function setAsChanged (changed = true) {
    isChangedRef.current = changed
  }

  function isChanged () {
    return isChangedRef.current
  }

  const _form = {
    submit: submit,
    reset: reset,
    onSubmit: onSubmit,
    removeOnSubmitListener: removeOnSubmitListener,
    onStartSubmit: onStartSubmit,
    removeOnStartSubmitListener: removeOnStartSubmitListener,
    onReset: onReset,
    removeOnResetListener: removeOnResetListener,
    setAsChanged: setAsChanged,
    isChanged: isChanged
  }

  const errors = operation ? operation.errors : observable.map()

  React.useEffect(() => {
    if (errors) {
      errors.clear()
    }
  }, [errors])

  return (
    <form className={props.className} ref={formRef} onSubmit={handleOnSubmit} encType='multipart/form-data'>
      <ErrorsContext.Provider value={errors}>
        <FormContext.Provider value={_form}>
          <Pipeline>
            <TriggerPipelineOnFormStartSubmit />
            {props.beforePipes}
            <CallbackOnPipe callback={(payload) => {
              return new Promise((resolve, reject) => {
                pipelineResolveRef.current = (p = {}) => {
                  resolve({ ...payload, ...p })
                }
                pipelineRejectRef.current = reject
              })
            }}
            />
            {props.resetOnSuccess === true ? (<ResetFormOnPipe />) : null}
            {props.children}
          </Pipeline>
        </FormContext.Provider>
      </ErrorsContext.Provider>
    </form>
  )
}

Form.defaultProps = {
  submitTo: 'store',
  resetOnSuccess: false,
  onSubmit: null,
  className: null,
  route: null,
  modelKeyName: 'id',
  parameterKeyName: null,
  withoutOperation: false,
  beforePipes: null,
  preventParentOperationRefresh: false
}

Form.propTypes = {
  submitTo: PropTypes.string,
  resetOnSuccess: PropTypes.bool,
  onSubmit: PropTypes.func,
  className: PropTypes.string,
  route: PropTypes.string,
  modelKeyName: PropTypes.string,
  parameterKeyName: PropTypes.string,
  withoutOperation: PropTypes.bool,
  beforePipes: PropTypes.node,
  preventParentOperationRefresh: PropTypes.bool
}

export default Form
